/**
 * @file lineItems.js
 * @version 1.10.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-14
 * @modified 2026-11-03
 * @description One line shape for the editable grid, and the translation to and
 *              from each document that uses it.
 *
 *              The grid (OrderLinesGrid) works on the customer order's own line
 *              shape — snake_case, `product_name` for the description,
 *              `unit_price` for whichever price the document is about. Invoices,
 *              purchase orders and bills each store lines differently:
 *
 *                invoice  snake_case, unit_price, line_total, account_code
 *                PO       camelCase JSONB, unitAmount, lineAmount, plus whatever
 *                         Xero put there (lineItemID, accountCode, tracking…)
 *                bill     snake_case, unit_amount, line_amount, account_code
 *
 *              Rather than teach the grid four vocabularies, each document maps
 *              in on load and out on save, here, where the mappings can be read
 *              side by side.
 *
 *              TWO RULES THE MAPPINGS KEEP
 *
 *              Line amounts are stored NET. A purchase order and a bill used to
 *              store qty × price + tax from the screen but net from Xero, so the
 *              same column meant two things — and the PO totals, which add tax on
 *              top of the line amounts, counted tax twice for anything typed in.
 *
 *              Additive names ride on the DESCRIPTION when saved ("Dairy Nut +
 *              Acid Buff"), because that is the text a supplier, a PDF and Xero
 *              see. In the grid they are their own column, so they are stripped
 *              from the description on load and put back on save. Without the
 *              strip, every edit would append them again.
 *
 *              1.10.0 (2026-11-03, Cam — typed lines name their accounts,
 *              utils/lineAccountRequired): a PO line keeps its accountCode in
 *              the grid's account_code, and a typed line (no item, not bought
 *              for an order line) sends the one chosen; an item line, or one
 *              bought for an order line, sends what it carried, as before.
 *
 *              1.6.0 (2026-10-14, Cam): a saved bill line on a PO line whose
 *              VAT is a penny or two off its amount × rate (a split line that
 *              took the rounding — utils/billLineSplit) keeps that VAT on
 *              load (`_vat`), so editing the bill does not move its total.
 *
 *              1.6.1 (2026-10-14, Cam — Lacto Production): a bill line keeps
 *              the unit its invoice printed (`bill_unit` in the grid, `unit`
 *              from the PDF reader / a saved line and in the payload —
 *              utils/billUnitConversion.billUnitOf), the evidence a bill per
 *              kg or per tonne is read in the PO line's unit by.
 *
 *              1.8.0 (2026-10-15, Cam): a bill line put in its PO line's
 *              quantity and unit carries the figures as invoiced — `_asBilled`
 *              in the grid, `as_billed` in the payload and on a saved line
 *              (utils/billLineChoices) — so it can be shown, undone and
 *              checked by the server.
 */

import { autoSelectSupplierFromItemCode } from './pricingHelpers';
import { getTaxRate } from './taxTypes';
import { resolveLineForItem, itemCodeOf, itemNameOf } from './customerPricing';
import { lineUnitsFromSaved } from './units';
import { isNoWeight } from './lineUnits';
// Stored figures go into the grid's inputs at 2-4dp: 236 reads 236.00, 236.125
// stays 236.125. Saving parses them back (num), so the strings are safe here.
import { formatDecimalInput } from './numberFormatters';
import { billUnitOf } from './billUnitConversion';

const num = (v) => {
  const x = parseFloat(v);
  return Number.isFinite(x) ? x : 0;
};

/** Money to the penny, as every document stores it. */
export const round2 = (v) => Math.round(num(v) * 100) / 100;

let seq = 0;
/** A stable React key for a line that has not been saved yet. */
export const newLineId = (prefix = 'new') => `${prefix}-${Date.now()}-${(seq += 1)}`;

// --- Additives -------------------------------------------------------------

/** Any additive shape (snake, camel, bare) → the picker's snake_case shape. */
export function normaliseAdditives(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((a) => {
      if (!a) return null;
      const id = a.additive_id ?? a.additiveId ?? a.id;
      if (!id) return null;
      const price = a.additive_price ?? a.additivePrice ?? a.price;
      return {
        additive_id: Number(id),
        additive_code: a.additive_code ?? a.additiveCode ?? a.code ?? '',
        additive_name: a.additive_name ?? a.additiveName ?? a.name ?? '',
        additive_price: price === null || price === undefined || price === '' ? null : Number(price),
      };
    })
    .filter(Boolean);
}

/** Snake_case additives → the camelCase shape purchase order JSONB carries. */
const toCamelAdditives = (list) => normaliseAdditives(list).map((a) => ({
  additiveId: a.additive_id,
  additiveCode: a.additive_code,
  additiveName: a.additive_name,
  additivePrice: a.additive_price,
}));

/**
 * "Dairy Nut" + [Acid Buff] → "Dairy Nut + Acid Buff". The same join the
 * server uses when it builds a purchase order from a customer order.
 */
export function withAdditiveNames(description, additives) {
  const base = String(description || '').trim();
  const names = normaliseAdditives(additives).map((a) => a.additive_name).filter(Boolean);
  return names.length ? [base, ...names].join(' + ') : base;
}

/**
 * The reverse, for loading: take the additive names back off the end of a
 * saved description, but only when they are exactly the names this line
 * carries, in order. Anything else is left alone — a description somebody
 * typed with a " + " in it is not an additive list.
 */
export function withoutAdditiveNames(description, additives) {
  const text = String(description || '');
  const names = normaliseAdditives(additives).map((a) => a.additive_name).filter(Boolean);
  if (!names.length) return text;
  const suffix = ` + ${names.join(' + ')}`;
  return text.endsWith(suffix) ? text.slice(0, -suffix.length) : text;
}

// --- Item → line -----------------------------------------------------------

/** The inventory row a saved line refers to, by id or failing that by code. */
export function findItemForLine(items, { id, code }) {
  if (!Array.isArray(items)) return null;
  if (id != null) {
    const byId = items.find((i) => i.id === id);
    if (byId) return byId;
  }
  const c = String(code || '').trim().toLowerCase();
  if (!c) return null;
  const matches = items.filter((i) => String(itemCodeOf(i)).toLowerCase() === c);
  // A handful of item codes are duplicated in inventory; the live one wins.
  return matches.find((i) => !i.is_archived) || matches[0] || null;
}

/**
 * What a purchase-side line becomes once an item is picked: the item's own
 * cost, purchase tax type and purchase account. There is no contract pricing
 * on this side — the supplier is fixed on the document, not chosen per line.
 */
export function resolvePurchaseLineForItem(item) {
  if (!item) return null;
  const taxType = item.purchase_tax_type ?? null;
  return {
    inventory_item_id: item.id ?? null,
    product_name: itemNameOf(item),
    product_code: itemCodeOf(item),
    unit_price: item.purchase_unit_price ?? '',
    tax_type: taxType,
    tax_rate: getTaxRate(taxType) || 0,
    account_code: item.purchase_account_code || item.cogs_account_code || '',
    additives: [],
  };
}

/** The item → line rule for a given grid variant. */
export function resolveItemForVariant(variant, item, pricing = {}) {
  if (!item) return null;
  if (variant === 'purchase' || variant === 'bill') return resolvePurchaseLineForItem(item);
  const resolved = resolveLineForItem({ item, ...pricing });
  if (variant === 'invoice') {
    return { ...resolved, account_code: item.sales_account_code || '' };
  }
  return resolved;
}

// --- Re-pricing for a new date -----------------------------------------------

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

const blankOrNumber = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};
const sameMoney = (a, b) => {
  const x = blankOrNumber(a);
  const y = blankOrNumber(b);
  if (x === null || y === null) return x === y;
  return Math.abs(x - y) < 0.00005;
};

/**
 * What a line's price is based on, in words: "Summer 2026 contract",
 * "January 2026 straights price", "Spot price", "List price".
 */
export function pricingBasisLabel(basis, contracts = []) {
  if (!basis) return 'Unknown';
  if (basis.source === 'contract') {
    const isMonth = basis.monthly_pricing_id != null;
    const c = (contracts || []).find(row => (isMonth
      ? row.month != null && row.id === basis.monthly_pricing_id
      : row.month == null && row.id === basis.contract_id));
    const month = c?.month ?? basis.month;
    const year = c?.year ?? basis.year;
    if (isMonth) return month && year ? `${MONTH_NAMES[month - 1]} ${year} straights price` : 'Straights price';
    return c?.period_name ? `${c.period_name} contract` : 'Contract';
  }
  if (basis.source === 'spot') return 'Spot price';
  if (basis.source === 'milk_powder') return 'Milk powder price';
  return 'List price';
}

/**
 * The lines whose price depends on the date and comes out differently on
 * `pricing.deliveryDate`, and what each would become.
 *
 * Only a line whose price came FROM the date is offered: a contract, spot or
 * milk powder price, or a line the new date would put on one. A line on the
 * item's list price before and after is left alone, even if someone typed its
 * price — the date has nothing to say about it.
 *
 *   - Recorded basis (source + contract / month id) differs from the new
 *     date's → offered, even at the same price: the line would be counted
 *     against a different contract.
 *   - Same basis → offered only for milk powder, which is priced per month.
 *   - No recorded basis (an invoice line loaded from the database) → offered
 *     when the new date prices it from a contract, spot or milk powder price
 *     and the price differs.
 *
 * Returns [{ lineId, index, code, name, before, after, patch }]. `patch` is what
 * to merge into the line to accept; everything else on the line — quantity,
 * description, supplier, additives — stays.
 */
export function repriceLinesForDate(lines, {
  variant = 'order',
  inventoryItems = [],
  contracts = [],
  spotPrices = [],
  milkPowderContracts = [],
  suppliers = [],
  deliveryDate,
  periodName,
  includeCost = false,
  lockedLineIds = null,
} = {}) {
  const out = [];
  (lines || []).forEach((line, index) => {
    if (!line?.inventory_item_id) return;
    if (lockedLineIds && lockedLineIds.has(line.id)) return;
    const item = (inventoryItems || []).find(i => i.id === line.inventory_item_id);
    if (!item) return;

    const next = resolveItemForVariant(variant, item, {
      contracts, spotPrices, milkPowderContracts, suppliers, deliveryDate, periodName,
      additives: line.additives || [],
    });
    if (!next) return;

    const nextBasis = {
      source: next.source,
      contract_id: next.contract_id ?? null,
      monthly_pricing_id: next.monthly_pricing_id ?? null,
      milk_powder_price_id: next.milk_powder_price_id ?? null,
      month: next.month ?? null,
      year: next.year ?? null,
    };
    const recorded = line.source
      ? {
          source: line.source,
          contract_id: line.contract_id ?? null,
          monthly_pricing_id: line.monthly_pricing_id ?? null,
          milk_powder_price_id: line.milk_powder_price_id ?? null,
          month: line.month ?? null,
          year: line.year ?? null,
        }
      : null;

    const priceDiffers = !sameMoney(line.unit_price, next.unit_price);
    const costDiffers = includeCost && !sameMoney(line.cost_price, next.cost_price);

    let offer;
    if (recorded) {
      const basisChanged = recorded.source !== nextBasis.source
        || recorded.contract_id !== nextBasis.contract_id
        || recorded.monthly_pricing_id !== nextBasis.monthly_pricing_id
        || (recorded.milk_powder_price_id != null && recorded.milk_powder_price_id !== nextBasis.milk_powder_price_id);
      offer = basisChanged || (nextBasis.source === 'milk_powder' && (priceDiffers || costDiffers));
    } else {
      offer = nextBasis.source !== 'inventory' && (priceDiffers || costDiffers);
    }
    if (!offer) return;

    const patch = {
      unit_price: next.unit_price,
      _autoPrice: next.unit_price,
      contract_id: next.contract_id ?? null,
      monthly_pricing_id: next.monthly_pricing_id ?? null,
      milk_powder_price_id: next.milk_powder_price_id ?? null,
      category_name: next.category_name ?? null,
      source: next.source,
      contract_number: next.contract_number ?? null,
      supplier_contract_number: next.supplier_contract_number ?? null,
      contracted_quantity: next.contracted_quantity ?? null,
      ordered_quantity: next.ordered_quantity ?? null,
      month: next.month ?? null,
      year: next.year ?? null,
    };
    if (includeCost) patch.cost_price = next.cost_price;

    out.push({
      lineId: line.id ?? null,
      index,
      code: itemCodeOf(item),
      name: line.product_name || itemNameOf(item),
      before: {
        price: blankOrNumber(line.unit_price),
        cost: includeCost ? blankOrNumber(line.cost_price) : undefined,
        basis: recorded ? pricingBasisLabel(recorded, contracts) : null,
      },
      after: {
        price: blankOrNumber(next.unit_price),
        cost: includeCost ? blankOrNumber(next.cost_price) : undefined,
        basis: pricingBasisLabel(nextBasis, contracts),
      },
      patch,
    });
  });
  return out;
}

/** Merge accepted re-pricing into the lines, matching by id (else position). */
export function applyRepricing(lines, rows) {
  return (lines || []).map((line, index) => {
    const row = (rows || []).find(r => (r.lineId != null ? r.lineId === line.id : r.index === index));
    return row ? { ...line, ...row.patch } : line;
  });
}

/** The header supplier an item's code suggests, when none is chosen yet. */
export const suggestedSupplierFor = (item, suppliers) =>
  autoSelectSupplierFromItemCode(itemCodeOf(item), suppliers);

// --- Invoices --------------------------------------------------------------

/** GET /api/invoices/:id/lines rows → grid lines. */
export function invoiceLinesFromApi(rows, items = []) {
  return (rows || []).map((l, i) => {
    const additives = normaliseAdditives(l.additives);
    const item = findItemForLine(items, { id: l.item_id, code: l.item_code });
    return {
      id: `inv-${l.id ?? i}`,
      // The stored line this row is. An edit to a document already in Xero
      // patches the matching Xero line by it (invoiceService._updatePostedInvoice);
      // a row added in the grid has none.
      invoice_line_id: l.id ?? null,
      inventory_item_id: item?.id ?? l.item_id ?? null,
      product_name: withoutAdditiveNames(l.description, additives),
      product_code: l.item_code || '',
      quantity: formatDecimalInput(l.quantity),
      unit_price: formatDecimalInput(l.unit_price),
      tax_type: l.tax_type || null,
      tax_rate: getTaxRate(l.tax_type) || 0,
      account_code: l.account_code || '',
      supplier_contact_id: l.supplier_contact_id ?? null,
      additives,
      // Not editable here, but it is on the line and a save re-inserts every
      // line — dropping it would quietly remove a discount.
      discount_amount: l.discount_amount ?? null,
      // The line's own unit weight: '' = the item's, '0' = None (no weight).
      unit_weight_kg: lineUnitsFromSaved({ unit_weight_kg: l.unit_weight_kg }).unit_weight_kg,
      // A credit line's stock effect (goods_returned / price_correction; null =
      // not chosen) — asked on save (utils/creditStockEffect), kept by an edit.
      credit_kind: l.credit_kind ?? null,
    };
  });
}

/** Grid lines → the invoice line_items payload. */
export function invoiceLinesToPayload(lines, amountsAre, lineTotals) {
  return lines.map((line) => {
    const { tax } = lineTotals(line, amountsAre);
    return {
      invoice_line_id: line.invoice_line_id ?? null,
      description: withAdditiveNames(line.product_name, line.additives),
      item_code: line.product_code || null,
      quantity: num(line.quantity),
      unit_price: num(line.unit_price),
      // As entered — the server works out net vs gross from line_amount_types.
      line_total: round2(num(line.quantity) * num(line.unit_price)),
      account_code: line.account_code || null,
      tax_type: line.tax_type || 'NONE',
      tax_amount: round2(tax),
      supplier_contact_id: line.supplier_contact_id || null,
      additives: normaliseAdditives(line.additives),
      discount_amount: line.discount_amount ?? null,
      // Local only (never sent to Xero): null = the item's, 0 = None.
      unit_weight_kg: isNoWeight(line.unit_weight_kg) ? 0
        : (num(line.unit_weight_kg) > 0 ? num(line.unit_weight_kg) : null),
      // A credit line's stock effect, as chosen when saving (null = not chosen).
      credit_kind: line.credit_kind ?? null,
    };
  });
}

// --- Purchase orders -------------------------------------------------------

/** PO JSONB line items → grid lines, keeping the original for the save. */
export function poLinesFromApi(rows, items = []) {
  return (rows || []).map((li, i) => {
    const additives = normaliseAdditives(li.additives);
    const code = li.itemCode ?? li.ItemCode ?? '';
    const item = findItemForLine(items, { code });
    return {
      id: li.lineItemID ? `po-${li.lineItemID}` : `po-${i}-${li.orderLineId || ''}`,
      inventory_item_id: item?.id ?? null,
      product_name: withoutAdditiveNames(li.description ?? li.Description, additives),
      product_code: code,
      quantity: formatDecimalInput(li.quantity),
      unit_price: formatDecimalInput(li.unitAmount),
      tax_type: li.taxType || null,
      tax_rate: getTaxRate(li.taxType) || 0,
      additives,
      haulier: li.haulier || '',
      driver: li.driver || '',
      vehicle: li.vehicle || '',
      // Its account (a typed line chooses one — utils/lineAccountRequired).
      account_code: li.accountCode || '',
      orderLineId: li.orderLineId || null,
      // What was supplied (the server's reading: the order line's, or a stock
      // line's own). Shown under the ordered quantity, and the line is costed
      // on it (Cam, 2026-10-12) — so the PO matches the supplier's bill.
      supplied_quantity: li.kind === 'haulage' || li.suppliedQuantity == null ? null : Number(li.suppliedQuantity),
      // The line's own units (blank = the item's), as the grid holds them.
      ...lineUnitsFromSaved({ unit_weight_kg: li.unitWeightKg, qty_per_pallet: li.qtyPerPallet, pallets: li.pallets }),
      // Everything Xero or the customer order put on the line that the grid
      // does not show — lineItemID, accountCode, tracking, discounts. Written
      // back untouched on save, so editing a synced PO no longer strips them.
      _source: li,
    };
  });
}

/*
 * Display-only fields the server adds to a PO line on the way out and works
 * out afresh every time — never saved back into the PO's JSON, where a stale
 * copy would outlive the choice it was made under.
 */
const PO_DISPLAY_ONLY = ['qtyDisplay', 'suppliedKey', 'suppliedSource', 'suppliedBlock'];
const withoutDisplayOnly = (src) => {
  const out = { ...(src || {}) };
  for (const k of PO_DISPLAY_ONLY) delete out[k];
  return out;
};

/** Grid lines → PO line_items payload. lineAmount is NET. */
export function poLinesToPayload(lines, lineTotals) {
  return lines.map((line) => {
    const { net, tax } = lineTotals(line, 'Exclusive');
    return {
      ...withoutDisplayOnly(line._source),
      orderLineId: line.orderLineId || null,
      description: withAdditiveNames(line.product_name, line.additives),
      itemCode: line.product_code || null,
      quantity: num(line.quantity),
      unitAmount: num(line.unit_price),
      taxType: line.tax_type || 'NONE',
      taxAmount: round2(tax),
      lineAmount: round2(net),
      additives: toCamelAdditives(line.additives),
      haulier: line.haulier || null,
      driver: line.driver || null,
      vehicle: line.vehicle || null,
      // A typed line's account, as chosen (Cam, 2026-11-03); an item line and
      // a line bought for an order line keep what they carried (_source).
      ...(!line.inventory_item_id && !line.orderLineId ? { accountCode: line.account_code || null } : {}),
      // Set on this line; null = the item's (the PDF and the PO-H fall back).
      // 0 = None — no weight, no pallets (utils/lineUnits).
      ...(isNoWeight(line.unit_weight_kg)
        ? { unitWeightKg: 0, qtyPerPallet: null, pallets: 0 }
        : {
          unitWeightKg: num(line.unit_weight_kg) > 0 ? num(line.unit_weight_kg) : null,
          qtyPerPallet: num(line.qty_per_pallet) > 0 ? num(line.qty_per_pallet) : null,
          pallets: line.pallets === '' || line.pallets == null ? null : num(line.pallets),
        }),
    };
  });
}

// --- Bills -----------------------------------------------------------------

/**
 * Bill lines from any of the three places they come from — a saved bill, the
 * PO-to-bill generator, or the PDF reader — into grid lines. All three are
 * snake_case with unit_amount; the PDF reader adds `unmatched`.
 */
/**
 * A bill line's quantity and unit price as billed: its own price, else its
 * amount ÷ its quantity (1 when none) — a haulier's charge printed only as an
 * amount (Burcombe's "Transport charge 33.50") still comes in at its cost.
 */
function billLineFigures(l) {
  const n = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
  const unit = n(l.unit_amount);
  const amount = n(l.line_amount);
  const qty = n(l.quantity);
  if ((unit === null || !Number.isFinite(unit)) && amount !== null && Number.isFinite(amount)) {
    const q = qty !== null && Number.isFinite(qty) && qty !== 0 ? qty : 1;
    return { quantity: formatDecimalInput(q), unit_price: formatDecimalInput(Math.round((amount / q) * 10000) / 10000) };
  }
  return { quantity: formatDecimalInput(l.quantity), unit_price: formatDecimalInput(l.unit_amount) };
}

export function billLinesFromApi(rows, items = [], { fromPdf = false } = {}) {
  return (rows || []).map((l, i) => {
    const additives = normaliseAdditives(l.additives);
    const item = findItemForLine(items, { id: l.item_id, code: l.item_code });
    return {
      id: l.id ? `bill-${l.id}` : newLineId(fromPdf ? 'pdf' : 'bill'),
      inventory_item_id: item?.id ?? null,
      product_name: withoutAdditiveNames(l.description, additives),
      product_code: l.item_code || '',
      ...billLineFigures(l),
      tax_type: l.tax_type || null,
      tax_rate: getTaxRate(l.tax_type) || 0,
      account_code: l.account_code || '',
      additives,
      // Which of the bill's POs this line bills, and which line of it.
      purchase_order_id: l.purchase_order_id ?? null,
      po_line_key: l.po_line_key ?? null,
      purchase_order_number: l.purchase_order_number ?? null,
      // The unit the invoice printed (kg / T / a pack), read by the PDF
      // reader and kept on the saved line — evidence for billUnitConversion.
      bill_unit: billUnitOf(l.unit),
      // Stored in its PO line's units (utils/billLineChoices): the figures as
      // invoiced, to show ("billed as …") and to put back.
      ...asBilledFromApi(l),
      ...savedVat(l),
      ...(fromPdf ? {
        _highlight: true,
        _hint: l.unmatched ? 'Read from the PDF — no matching inventory item' : 'Read from the PDF',
      } : {}),
    };
  });
}

/** A saved line put in its PO line's units: its figures as invoiced, and how to undo it. */
function asBilledFromApi(l) {
  const as = l?.as_billed && typeof l.as_billed === 'object' ? l.as_billed : null;
  if (!as || !(num(as.quantity) > 0) || !(num(as.unit_amount) > 0)) return {};
  return {
    _asBilled: { ...as },
    _choices: { units: 'po' },
    _before: { units: { quantity: formatDecimalInput(as.quantity), unit_price: formatDecimalInput(as.unit_amount), bill_unit: billUnitOf(as.unit) } },
  };
}

/**
 * A saved bill line's own VAT, when it is not its amount × rate — a line split
 * over several PO lines that took the penny of rounding (utils/billLineSplit).
 * Only a saved line on a PO line, and only a rounding (2p at most): anything
 * else is worked out afresh, as it always was.
 */
function savedVat(l) {
  if (!l?.id || !l.purchase_order_id || !l.po_line_key) return {};
  const net = parseFloat(l.line_amount);
  const tax = parseFloat(l.tax_amount);
  if (!Number.isFinite(net) || !Number.isFinite(tax)) return {};
  const worked = round2(net * (getTaxRate(l.tax_type) || 0));
  const off = Math.abs(tax - worked);
  if (off < 0.005 || off > 0.025) return {};
  return { _vat: { net: round2(net), tax: round2(tax), tax_type: l.tax_type || null } };
}

/** Grid lines → bill line_items payload. line_amount is NET, as Xero stores it. */
export function billLinesToPayload(lines, lineTotals) {
  return lines.map((line) => {
    const { net, tax } = lineTotals(line, 'Exclusive');
    return {
      description: withAdditiveNames(line.product_name, line.additives),
      item_code: line.product_code || null,
      quantity: num(line.quantity),
      unit_amount: num(line.unit_price),
      tax_type: line.tax_type || 'NONE',
      tax_amount: round2(tax),
      line_amount: round2(net),
      account_code: line.account_code || null,
      additives: normaliseAdditives(line.additives),
      purchase_order_id: line.purchase_order_id ?? null,
      po_line_key: line.purchase_order_id ? (line.po_line_key ?? null) : null,
      // The saved line this row is (billLinesFromApi's `bill-<id>`): an edit
      // of a bill in Xero changes that line in place, keeping its Xero line.
      bill_line_id: billLineIdOf(line.id),
      unit: line.bill_unit || null,
      // Put in its PO line's units: the figures as invoiced (checked again on save).
      ...(line._asBilled && line.purchase_order_id ? { as_billed: { ...line._asBilled } } : {}),
    };
  });
}

/** The bill_lines id behind a grid row id `bill-<id>`, or null for a new row. */
function billLineIdOf(id) {
  const m = /^bill-(\d+)$/.exec(String(id || ''));
  return m ? Number(m[1]) : null;
}
